import axios from 'axios'
import qs from 'qs'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import crypto from 'crypto-js'
import type { BasicResultTsp } from '@/api/model/baseModel'
import { UserStore } from '@/store/modules/users'
import { CustomOptions } from '@/api/model/httpModel'
import { RequestDataParams } from '#/bridge'

interface BasicResutlTspApi<T> {
  code: string
  description: string
  data: T
}

// TSP 测试环境 key和秘钥
const APP_KEY = '4357637072'
const APP_SECRET = '7e324716e614fdd86473b5e1df87bf52'

const baseURL = import.meta.env.VITE_BASE_URL_TSP

const getNonce = () => {
  const str = '0123456789abcdefghijklmnopqrstvuwxyz'
  let nonce = ''
  for (let i = 0; i < 16; i++) {
    nonce += str[Math.floor(Math.random() * (str.length - 1))]
  }
  return nonce
}

const getReqHeader = (config: any) => {
  const timestamp = new Date().getTime()
  const nonce = getNonce()
  let signatureArr = [
    `bt-auth-appkey:${APP_KEY}`,
    `bt-auth-nonce:${nonce}`,
    `bt-auth-timestamp:${timestamp}`,
  ]

  let paramJson = {}
  if (config.method.toLowerCase() === 'post' && config.data) {
    paramJson['json'] = JSON.stringify(config.data)
  }
  // 处理url参数
  if (qs.stringify(config.params)) {
    paramJson = {
      ...paramJson,
      ...config.params,
    }
  }

  let param = ''
  Object.keys(paramJson)
    .sort()
    .map((item: string) => {
      param += `${item}=${paramJson[item]}`
    })

  let signStr = `${config.method.toUpperCase()}${config.url}${signatureArr.join(
    '',
  )}${param}${APP_SECRET}`
  signStr = encodeURIComponent(signStr).replace(/\%20/g, '')
  const signature = crypto.SHA256(signStr).toString()

  return {
    'bt-auth-appkey': APP_KEY,
    'bt-auth-nonce': nonce,
    'bt-auth-timestamp': timestamp + '',
    'bt-auth-sign': signature,
  }
}

const httpClient = (baseConfig: AxiosRequestConfig, options: CustomOptions) => {
  const service: AxiosInstance = axios.create({
    baseURL: baseURL,
    withCredentials: false,
    timeout: 10000,
  })

  service.interceptors.request.use(
    (config: AxiosRequestConfig) => {
      const userStore = UserStore()

      const customHeaders = getReqHeader(config)

      config.headers = {
        ...config.headers,
        accessToken: userStore.token,
        cVer: userStore.appVersion as any,
        terminal: 'GW_APP_GWM',
        tokenId: userStore.userInfo.tokenId,
        tenantId: '1',
        beanId: userStore.userInfo.beanId,
        brand: '10',
        rs: '2',
        enterPriseId: 'CC01',
        ...customHeaders,
      }

      config.params = {
        ...config.params,
      }

      if (config.method?.toLowerCase() === 'post') {
        if (!config.data) {
          config.data = {}
        }
      }

      const cancelToken = axios.CancelToken
      // 取消请求
      if (options.cancel) {
        config.cancelToken = new cancelToken((handle) => {
          options.cancel && options.cancel(handle)
        })
      }

      return config
    },
    (error) => {
      return Promise.reject(error)
    },
  )

  service.interceptors.response.use(
    (response: AxiosResponse<BasicResutlTspApi<any>, AxiosRequestConfig>) => {
      const { data } = response
      if (data.code !== '000000') {
        return Promise.reject({
          isSuccess: false,
          result: data,
        })
      } else {
        return Promise.resolve(response)
      }
    },
    () => {
      return Promise.reject({
        isSuccess: false,
        result: {
          code: '999',
          message: '服务开小差了，请稍后重试',
        },
      })
    },
  )
  return service(baseConfig)
}
const request = <T extends { code: string; description: string; data: T } = any>(
  config: AxiosRequestConfig,
  options: CustomOptions = {
    showToast: true,
    showNetworkToast: true,
    customHeaders: [],
  },
): Promise<BasicResultTsp<T>> => {
  return new Promise((resolve) => {
    httpClient(config, options)
      .then((res: AxiosResponse<T, AxiosRequestConfig>) => {
        resolve({
          isSuccess: true,
          result: res.data,
        })
      })
      .catch((error) => {
        resolve(error)
      })
  })
}

export const requestTsp = (config: RequestDataParams) => {
  return request({
    url: config.url,
    method: config.method.toUpperCase() as 'GET' | 'POST',
    data: config.params,
  })
}
